import { Fragment, Key, ReactElement, ReactNode, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import { Loading, ShowMorePagination } from '@ebsco-ui/ebsco-ui';

import { PageErrorMessage, PageTitle } from '@app/components';
import { usePaginationItemFocus, useQueryParams } from '@app/hooks';
import { generateRouteParams } from '@app/utils';
import { getPageMessages, sharedMessages } from '@app/translations';
import { focusedElementSelector, Resource, SetState } from '@app/constants';

import { recordsPerPage } from './constants';
import { BrowseBy } from './BrowseBy';
import { BrowseSearch } from '../BrowseSearch';

import css from './Browse.module.scss';

interface TableClasses {
  row?: string;
  cell?: string;
}

interface Messages {
  documentTitle: MessageDescriptor;
  pageTitle: MessageDescriptor;
  searchBarPlaceholder: MessageDescriptor;
  showMoreButtonTitle: MessageDescriptor;
  paginationTitle?: MessageDescriptor;
}

export type BrowseColumn<T> = {
  label: string;
  formatter: (record: T) => ReactNode;
};

interface BrowseProps<PageType, QueryResultType> {
  messages: Messages;
  resource: Resource<PageType, QueryResultType>;
  columns: BrowseColumn<PageType>[];
  pageNumber: number;
  tableClasses?: TableClasses;
  isDataLoadedOnInitialRender?: boolean;
  shouldRenderBrowseBy?: boolean;
  setPageNumber: SetState<number>;
  loadMore: () => void;
  renderEmptyState: () => ReactElement;
  shouldRenderSpecialRow?: (...args: unknown[]) => boolean;
  renderSpecialRow?: ({ record, classes }) => ReactElement;
  renderBrowseTitle?: () => ReactElement;
}

export const Browse = <PageType, QueryResultType>({
  messages: {
    documentTitle,
    pageTitle,
    searchBarPlaceholder,
    showMoreButtonTitle,
  },
  resource: {
    queryResult: { isFetching, isError, refetch, isFetched },
    totalRecords,
    recordKey,
    pages,
  },
  columns,
  pageNumber,
  tableClasses,
  isDataLoadedOnInitialRender = true,
  shouldRenderBrowseBy = false,
  setPageNumber,
  loadMore,
  renderEmptyState,
  shouldRenderSpecialRow = () => false,
  renderSpecialRow,
  renderBrowseTitle,
}: BrowseProps<PageType, QueryResultType>): ReactElement => {
  const { $t } = useIntl();
  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prevSearchTermRef = useRef(queryParams.search as string);
  const { shouldBeFocused } = usePaginationItemFocus({
    pageNumber,
    totalPagesNumber: pages.length,
    selector: focusedElementSelector,
  });

  const uniquePaginationName = pageTitle.id?.replace('.title', '') as string;

  const classes = {
    headerRow: cn(
      css.headerRow,
      tableClasses?.row && {
        [tableClasses.row]: Boolean(tableClasses),
      }
    ),
    row: cn(
      css.row,
      tableClasses?.row && {
        [tableClasses.row]: Boolean(tableClasses),
      }
    ),
    cell: cn(
      css.cell,
      tableClasses?.cell && {
        [tableClasses.cell]: Boolean(tableClasses),
      }
    ),
  };

  const renderLoading = () => (
    <div data-auto="browse-spinner" className={css.pageSpinner}>
      <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
    </div>
  );

  const handleSearch = (term: string) => {
    if (
      term !== prevSearchTermRef.current ||
      term !== (queryParams.search as string)
    ) {
      prevSearchTermRef.current = term;
      setPageNumber(1);
      navigate(
        generateRouteParams({
          pathname: location.pathname,
          ...(term && {
            query: {
              ...queryParams,
              search: term,
            },
          }),
        })
      );
    }
  };

  const renderPageContent = () => {
    if (pageNumber === 1 && isFetching) {
      return renderLoading();
    }

    if (isError) {
      return <PageErrorMessage onClick={refetch} />;
    }

    if (!isDataLoadedOnInitialRender && !queryParams.search) {
      return renderEmptyState();
    }

    if (isFetched && totalRecords === 0) {
      return renderEmptyState();
    }

    return (
      !isEmpty(pages[0]) && (
        <>
          {renderBrowseTitle?.()}
          <div
            className={css.list}
            role="grid"
            aria-label={$t(pageTitle)}
            aria-colcount={columns.length}
            tabIndex={0}
          >
            <div className={css.body}>
              <ShowMorePagination
                pagesContainerTag="div"
                uniqueName={uniquePaginationName}
                className={css.scrollContainer}
                error={undefined}
                messages={getPageMessages($t, {
                  showMoreButtonTitle: $t(showMoreButtonTitle),
                })}
                isLoading={isFetching}
                totalRecords={totalRecords}
                pages={pages}
                onLoadPage={loadMore}
              >
                {(records: PageType[], currentPageNumber) => (
                  <Fragment key={currentPageNumber}>
                    {currentPageNumber === 0 && (
                      <div role="rowgroup">
                        <div className={classes.headerRow} role="row">
                          {columns.map(
                            column =>
                              column && (
                                <div
                                  className={classes.cell}
                                  role="columnheader"
                                  key={column.label}
                                >
                                  {column.label}
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}
                    {records.map((record, index) => (
                      <div
                        role="rowgroup"
                        key={record[recordKey] as Key}
                        {...(shouldBeFocused &&
                          !index && {
                            tabIndex: -1,
                            'data-focus': 'focusedNode',
                          })}
                      >
                        <div
                          className={cn(classes.row, {
                            [css.highlightedRow]: record['isAnchor'],
                          })}
                          role="row"
                          data-auto="row"
                          aria-rowindex={
                            currentPageNumber * recordsPerPage + index + 1
                          }
                        >
                          {renderSpecialRow && shouldRenderSpecialRow(record)
                            ? renderSpecialRow({
                                record,
                                classes,
                              })
                            : columns.map(
                                (column, columnIndex) =>
                                  column && (
                                    <div
                                      role="cell"
                                      className={classes.cell}
                                      key={columnIndex}
                                    >
                                      {column.formatter(record)}
                                    </div>
                                  )
                              )}
                        </div>
                      </div>
                    ))}
                  </Fragment>
                )}
              </ShowMorePagination>
            </div>
          </div>
        </>
      )
    );
  };

  return (
    <>
      <Helmet>
        <title>{$t(documentTitle)}</title>
      </Helmet>
      <div
        className={cn(css.container, 'container')}
        data-auto="browse-container"
      >
        <PageTitle>{$t(pageTitle)}</PageTitle>
        <div>
          <BrowseSearch
            label={$t(pageTitle)}
            placeholder={$t(searchBarPlaceholder)}
            onSearch={handleSearch}
          />
          {shouldRenderBrowseBy && (
            <>
              <BrowseBy fieldName="departments" />
              <BrowseBy fieldName="instructors" />
            </>
          )}
          {renderPageContent()}
        </div>
      </div>
    </>
  );
};
